import React, { createContext, useContext, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const night = '#14110E';
const wood = '#241E18';
const edge = '#3D3428';
const parchment = '#F3EDE2';
const dust = '#A89B8C';
const sunset = '#E85D4C';
const gold = '#C9A227';
const moss = '#6B8F71';
const teal = '#3D7A8C';
const deepRed = '#A33B32';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: sunset,
      light: '#F08A7A',
      dark: '#C44738',
      contrastText: night,
    },
    secondary: {
      main: gold,
      light: '#E4C56A',
      dark: '#8C7010',
      contrastText: night,
    },
    success: {
      main: moss,
      light: '#8AAB90',
      dark: '#4E6B54',
    },
    info: {
      main: teal,
      light: '#6AA3B3',
      dark: '#2A5563',
    },
    warning: {
      main: gold,
      light: '#E4C56A',
      dark: '#8C7010',
    },
    error: {
      main: deepRed,
      light: '#C45A50',
      dark: '#7A2B24',
    },
    background: {
      default: night,
      paper: wood,
    },
    text: {
      primary: parchment,
      secondary: dust,
    },
    divider: edge,
  },
  typography: {
    fontFamily: '"Outfit", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h2: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h3: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    h4: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Barlow Condensed", "Outfit", sans-serif',
      fontWeight: 600,
    },
    button: {
      fontFamily: '"Outfit", "Helvetica", "Arial", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    body1: {
      lineHeight: 1.6,
    },
    body2: {
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: night,
          color: parchment,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          fontWeight: 600,
          transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: wood,
          backgroundImage: 'none',
          borderRadius: 4,
          border: `1px solid ${edge}`,
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: wood,
          backgroundImage: 'none',
          borderRadius: 4,
          border: `1px solid ${edge}`,
          boxShadow: 'none',
          transition: 'border-color 0.2s ease',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: wood,
          backgroundImage: 'none',
          borderBottom: `1px solid ${gold}`,
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
        },
      },
    },
  },
});

export const CustomThemeProvider = ({ children }) => {
  const [isDarkMode] = useState(true);

  const toggleTheme = () => {
  };

  const theme = darkTheme;

  const contextValue = {
    isDarkMode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
